import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../layouts/Layout';
import PropertyCard from '../components/PropertyCard';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import TimelineMetric from '../components/ui/TimelineMetric';
import { Home, Eye, MessageSquare, Plus, TrendingUp } from 'lucide-react';
import dashboardIcon from '../assets/img3d/dashboard.png';
import homeIcon from '../assets/img3d/home.png';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DashboardPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState({
    totalProperties: 0,
    published: 0,
    draft: 0,
    totalViews: 0,
    totalContacts: 0
  });
  const [properties, setProperties] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [selectedBuildingId, setSelectedBuildingId] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
    fetchProperties();
    fetchBuildings();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/professional/stats`);
      const data = response.data;
      // Support both camelCase (API) and snake_case (legacy)
      setStats({
        totalProperties: data.totalProperties || data.total_properties || 0,
        published: data.published || 0,
        draft: data.draft || 0,
        totalViews: data.totalViews || data.total_views || 0,
        totalContacts: data.totalContacts || data.total_contacts || 0
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
      setStats({
        totalProperties: 0,
        published: 0,
        draft: 0,
        totalViews: 0,
        totalContacts: 0
      });
    }
  };

  const fetchProperties = async () => {
    try {
      const params = selectedBuildingId ? { buildingId: selectedBuildingId } : undefined;
      const response = await axios.get(`${API}/professional/properties`, { params });
      // Handle both array and object response formats
      const data = response.data;
      const propertiesArray = Array.isArray(data) ? data : (data?.items || []);
      setProperties(propertiesArray);
    } catch (error) {
      console.error('Error fetching properties:', error);
      setProperties([]); // Ensure properties is always an array
    } finally {
      setLoading(false);
    }
  };

  const fetchBuildings = async () => {
    try {
      const response = await axios.get(`${API}/professional/buildings`);
      setBuildings(response.data || []);
    } catch (error) {
      console.error('Error fetching buildings:', error);
      setBuildings([]);
    }
  };

  return (
    <Layout
      headerConfig={{
        icon: homeIcon,
        iconColor: 'bg-white',
        title: 'Mes Propriétés',
        subtitle: `Gérez vos annonces et suivez vos performances`,
        primaryAction: 'Tableau de Bord',
        secondaryAction: 'Nouvelle annonce',
        tertiaryAction: undefined,
        onPrimaryAction: () => navigate('/professional'),
        onSecondaryAction: () => navigate('/add-property'),
        onTertiaryAction: undefined
      }}
    >
      {/* Top KPIs - Timeline Metrics */}
      <div className="mb-8 w-full">
        <div className="bg-white rounded-lg border border-neutral-200 overflow-hidden w-full">
          <div className="flex flex-col md:flex-row w-full">
            <div className="flex-1">
              <TimelineMetric
                icon={Home}
                value={stats.totalProperties}
                label="Total annonces"
                type="properties"
              />
            </div>
            <div className="flex-1">
              <TimelineMetric
                icon={TrendingUp}
                value={stats.published}
                label="Publiées"
                type="properties"
              />
            </div>
            <div className="flex-1">
              <TimelineMetric
                icon={Home}
                value={stats.draft}
                label="Brouillons"
                type="arrears"
              />
            </div>
            <div className="flex-1">
              <TimelineMetric
                icon={Eye}
                value={stats.totalViews}
                label="Total vues"
                type="occupancy"
              />
            </div>
            <div className="flex-1">
              <TimelineMetric
                icon={MessageSquare}
                value={stats.totalContacts}
                label="Contacts"
                type="owners"
                noBorder={true}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Properties Section */}
      <Card>
        <CardContent className="p-6">
       

          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Mes annonces</h2>
            {buildings.length > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Filtrer par immeuble :</span>
                <select
                  className="border rounded-md px-2 py-1 text-sm bg-white"
                  value={selectedBuildingId}
                  onChange={(e) => {
                    setSelectedBuildingId(e.target.value);
                    setLoading(true);
                    fetchProperties();
                  }}
                >
                  <option value="">Tous les immeubles</option>
                  {buildings.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <Tabs defaultValue="all" className="w-full">
            <TabsList className="mb-6">
              <TabsTrigger value="all">
                Toutes ({Array.isArray(properties) ? properties.length : 0})
              </TabsTrigger>
              <TabsTrigger value="published">
                Publiées ({Array.isArray(properties) ? properties.filter(p => {
                  const status = p.status || '';
                  return status.toUpperCase() === 'PUBLISHED' || status.toLowerCase() === 'published';
                }).length : 0})
              </TabsTrigger>
              <TabsTrigger value="draft">
                Brouillons ({Array.isArray(properties) ? properties.filter(p => {
                  const status = p.status || '';
                  return status.toUpperCase() === 'DRAFT' || status.toLowerCase() === 'draft';
                }).length : 0})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="all">
              {loading ? (
                <div className="text-center py-12">
                  <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF9F68]"></div>
                </div>
              ) : Array.isArray(properties) && properties.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {properties.map((property) => {
                    // Support both camelCase (Prisma) and snake_case (legacy)
                    const viewsCount = property.viewsCount || property.views_count || 0;
                    return (
                      <div key={property.id} className="relative">
                        <PropertyCard property={property} hideFavorite={true} />
                        {viewsCount > 0 && (
                          <div className="absolute top-3 right-3 flex flex-col gap-2">
                            <div className="bg-white/90 backdrop-blur-sm rounded-full px-3 py-1 text-xs font-semibold">
                              {viewsCount} vues
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Home className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Aucune annonce
                  </h3>
                  <p className="text-gray-600 mb-6">
                    Commencez par créer votre première annonce
                  </p>
                  <Button
                    onClick={() => navigate('/add-property')}
                    className="bg-[#FF9F68] hover:bg-[#E67E4F] text-white"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Créer une annonce
                  </Button>
                </div>
              )}
            </TabsContent>

            <TabsContent value="published">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {Array.isArray(properties) && properties
                  .filter(p => {
                    const status = p.status || '';
                    return status.toUpperCase() === 'PUBLISHED' || status.toLowerCase() === 'published';
                  })
                  .map((property) => {
                    const viewsCount = property.viewsCount || property.views_count || 0;
                    return (
                      <div key={property.id} className="relative">
                        <PropertyCard property={property} hideFavorite={true} />
                        {viewsCount > 0 && (
                          <div className="absolute top-3 right-3 flex flex-col gap-2">
                            <div className="bg-white/90 backdrop-blur-sm rounded-full px-3 py-1 text-xs font-semibold">
                              {viewsCount} vues
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
            </TabsContent>

            <TabsContent value="draft">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {Array.isArray(properties) && properties
                  .filter(p => {
                    const status = p.status || '';
                    return status.toUpperCase() === 'DRAFT' || status.toLowerCase() === 'draft';
                  })
                  .map((property) => {
                    const viewsCount = property.viewsCount || property.views_count || 0;
                    return (
                      <div key={property.id} className="relative">
                        <PropertyCard property={property} hideFavorite={true} />
                        {viewsCount > 0 && (
                          <div className="absolute top-3 right-3 flex flex-col gap-2">
                            <div className="bg-white/90 backdrop-blur-sm rounded-full px-3 py-1 text-xs font-semibold">
                              {viewsCount} vues
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </Layout>
  );
};

export default DashboardPage;
